import React, { useState } from 'react';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    FormHelperText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { FlexyForm, FormObject, FormStructure } from '@europrocurement/flexy-form';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { UpdateDossierPayload } from '../../../types/UpdateDossierPayload';

type EditFolioSocieteRefFormProps = {
    dossier: DossierOpenSearch;
    onSuccess: () => void;
    onCancel?: () => void;
    updateDossier: (payload: UpdateDossierPayload) => Promise<void> | void;
};

export const EditFolioSocieteRefForm: React.FunctionComponent<EditFolioSocieteRefFormProps> =
    function ({ dossier, onSuccess, onCancel, updateDossier }) {
        const { palette, breakpoints } = useTheme();
        const downLgBreakpoint = useMediaQuery(breakpoints.down('lg'));
        const downSmBreakpoint = useMediaQuery(breakpoints.down('sm'));

        const [loading, setLoading] = useState(false);

        const formContext = useForm<FormObject>({
            mode: 'onChange',
            defaultValues: {
                societeRef: dossier.societeRef || '',
            },
        });

        const textInputStyle = {
            boxSizing: 'border-box',
            height: '56px',
            padding: '12px 20px',
            borderRadius: '12px',
            fontFamily: 'Inter',
            fontSize: '16px',
            fontWeight: '400',
            lineHeight: '32px',
        };

        const commonInputProps = {
            inputProps: {
                sx: textInputStyle,
            },
        };

        const onSubmit = async (formData: FormObject) => {
            setLoading(true);
            const { setError } = formContext;

            try {
                const newRef = ((formData.societeRef as string) || '').trim();

                // Validation basique
                if (!newRef) {
                    setError('societeRef', {
                        type: 'required',
                        message: 'Vous devez renseigner une référence client',
                    });
                    return;
                }

                if (!dossier.id) {
                    setError('root', {
                        type: 'custom',
                        message: 'ID du dossier manquant',
                    });
                    return;
                }

                // Mise à jour avec attente de la confirmation
                await updateDossier({
                    societeRef: newRef,
                });

                // Succès : onSuccess sera appelé pour fermer la modale et refresh
                onSuccess();
            } catch (error) {
                console.error('Erreur lors de la mise à jour:', error);
                setError('root', {
                    type: 'custom',
                    message: 'Une erreur est survenue lors de la sauvegarde',
                });
            } finally {
                setLoading(false);
            }
        };

        const handleCancel = () => {
            if (onCancel) onCancel();
        };

        const formStructure: FormStructure[] = [
            {
                type: 'text',
                name: 'societeRef',
                inputlabel: 'Référence client',
                rules: {
                    required: 'Vous devez renseigner une référence client',
                },
                placeholder: 'Ex: REF-2025-001',
                ...commonInputProps,
            },
        ];

        const formProps = {
            formContext,
            formObject: formContext.getValues(),
            onSubmit,
            submitButton: {
                displayed: false,
            },
        };

        const renderSubmitButton = () => (
            <Box
                sx={{
                    backgroundColor: palette.background.dark,
                    position: 'sticky',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    display: 'flex',
                    flexDirection: downLgBreakpoint ? 'column' : 'row',
                    justifyContent: 'center',
                    gap: '16px',
                    height: downLgBreakpoint ? 'auto' : '100px',
                    alignItems: 'center',
                    padding: downLgBreakpoint ? '20px' : '',
                }}
            >
                <Button
                    onClick={handleCancel}
                    sx={{
                        color: palette.text.primary,
                        padding: '16px 24px 16px 24px',
                        width: downLgBreakpoint ? '100%' : 'auto',
                        display: downLgBreakpoint ? 'block' : 'flex',
                    }}
                >
                    Annuler
                </Button>
                <Button
                    data-testid="submit-form"
                    type="submit"
                    disabled={loading}
                    variant="contained"
                    sx={{
                        backgroundColor: palette.primary.main,
                        color: palette.primary.contrastText,
                        padding: '16px 24px 16px 24px',
                        width: downLgBreakpoint ? '100%' : 'auto',
                        display: downLgBreakpoint ? 'block' : 'flex',
                    }}
                    onClick={formContext.handleSubmit(onSubmit)}
                >
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
            </Box>
        );

        let formWidth = downLgBreakpoint ? 'calc(100vw - 200px)' : '1098px';
        if (downSmBreakpoint) formWidth = '100%';

        return (
            <Box
                sx={{
                    width: formWidth,
                    margin: '0 auto',
                }}
            >
                <Backdrop
                    sx={(backDropTheme) => ({
                        color: 'common.white',
                        zIndex: backDropTheme.zIndex.drawer + 1,
                    })}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>

                <Box
                    sx={{
                        width: downLgBreakpoint ? '100%' : '60%',
                        maxWidth: downLgBreakpoint ? '100%' : '560px',
                        margin: '0 auto',
                    }}
                >
                    <Box sx={{ marginBottom: '10px' }}>
                        <Typography variant="l2dh6">Modifier la référence client</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {formContext.formState.errors.root?.message && (
                            <FormHelperText error>
                                {formContext.formState.errors.root.message}
                            </FormHelperText>
                        )}

                        <FlexyForm
                            onSubmit={onSubmit}
                            formObject={formProps.formObject}
                            formStructure={formStructure}
                            formContext={formContext}
                            submitButton={{ displayed: false, render: renderSubmitButton }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    };

export default EditFolioSocieteRefForm;
